import type { Filters, ReasonNode, Session } from './types'
import { kindChannelOf, verificationLive } from './types'

export interface Visibility {
  visible: Set<string>
  /**
   * Per collapsed node: how many marks appear if you unfold it *once*, under
   * the current filters and playhead.
   *
   * "Once" is load-bearing. A nested fold counts as the single stub it shows,
   * so an outer `›6` can unfold to reveal a `›22`. That is the honest number:
   * it promises what one unfold reveals, not how much is down there.
   */
  folded: Map<string, number>
}

/**
 * The display filters, as a predicate. The seed is never filtered out.
 *
 * The kind switches read `kindChannelOf` and `n.terminal` — deliberately NOT
 * `channelOf`. They are named after kinds of reasoning move, so they have to
 * hide exactly those moves: a switch labelled "Contradictions" that leaves a
 * contradiction on screen because a source happened to dispute it is a switch
 * nobody can trust. The `disputed` switch is the one that reads the override —
 * the same live-verification fact that recolours the node — so a disputed
 * contradiction is hidden when EITHER of its switches is off, which is exactly
 * the two groups it belongs to.
 *
 * One exemption, unconditional: a node carrying a recorded outcome is always
 * drawn. It is the only thing in the graph that did not come from the model, and
 * no display preference should be able to make it disappear.
 *
 * Read off `switchesHiding`, so the predicate and the list of switches a press
 * would have to turn back on are one reading (D335): two copies of this ladder
 * is how "Show it" comes to turn on a switch that was not the one hiding it.
 */
export function passesFilters(n: ReasonNode, filters: Filters): boolean {
  return switchesHiding(n, filters).length === 0
}

/** The seven switches the legend's colour rows carry — every filter that hides
 *  a node. Signal flow and labels are switches too, but they hide nothing. */
export type ChannelKey = Exclude<keyof Filters, 'particles' | 'labels'>
export const CHANNEL_KEYS: readonly ChannelKey[] = [
  'supported',
  'contradictions',
  'gaps',
  'insights',
  'disputed',
  'weak',
  'dead',
]

const NONE: readonly ChannelKey[] = Object.freeze([])

/**
 * Which switches, each on its own, keep this node off the canvas — empty when it
 * passes. A disputed contradiction lists both, because turning on either alone
 * would still leave it hidden. The shared empty answer keeps the common case,
 * a node that passes, from allocating on every visibility pass.
 */
export function switchesHiding(n: ReasonNode, filters: Filters): readonly ChannelKey[] {
  if (n.parentId === null) return NONE
  if (n.outcome) return NONE
  let out: ChannelKey[] | null = null
  const add = (k: ChannelKey) => (out ??= []).push(k)
  if (!filters.disputed && n.verification?.reading === 'refuted' && verificationLive(n)) {
    add('disputed')
  }
  const ch = kindChannelOf(n)
  if (ch === 'signal' && !filters.supported) add('supported')
  if (ch === 'tension' && !filters.contradictions) add('contradictions')
  if (ch === 'gap' && !filters.gaps) add('gaps')
  if (ch === 'insight' && !filters.insights) add('insights')
  if (ch === 'weak' && !filters.weak) add('weak')
  if (n.terminal && !filters.dead) add('dead')
  return out ?? NONE
}

/**
 * Every colour switch on, and the canvas switches — signal flow, labels — left
 * as they were: the one-press way back from a legend row pressed to learn what
 * it meant (D335). The same object when nothing was off, so a press that changes
 * nothing writes nothing.
 */
export function withChannelsOn(filters: Filters): Filters {
  if (CHANNEL_KEYS.every((k) => filters[k])) return filters
  const next = { ...filters }
  for (const k of CHANNEL_KEYS) next[k] = true
  return next
}

/**
 * The filters with every switch that hides this node turned back on — its own,
 * and every ancestor's, since hiding a node hides everything below it
 * (`computeVisible`). Only those switches: the reader's other choices stand.
 * The same object when no switch is hiding it.
 */
export function filtersShowing(session: Session, filters: Filters, id: string): Filters {
  let next: Filters | null = null
  let cur: string | null = id
  const guard = new Set<string>()
  while (cur && !guard.has(cur)) {
    guard.add(cur)
    const n: ReasonNode | undefined = session.nodes[cur]
    if (!n) break
    for (const k of switchesHiding(n, next ?? filters)) (next ??= { ...filters })[k] = true
    cur = n.parentId
  }
  return next ?? filters
}

/**
 * What an off switch is currently hiding that the user would want to know about.
 *
 * The counterpart to the exemption above: a refuted node is filterable like any
 * other, so the sidebar has to be able to say that one is behind a switch rather
 * than letting the strongest finding in the exploration disappear in silence.
 */
export function hiddenFindings(session: Session, filters: Filters): number {
  let n = 0
  for (const id of session.order) {
    const node = session.nodes[id]
    if (!node || node.parentId === null) continue
    if (passesFilters(node, filters)) continue
    if (node.verification?.reading === 'refuted') n++
  }
  return n
}

/**
 * Which nodes are on screen, given the display filters, the timeline and the
 * folds — and how much sits behind each fold.
 *
 * Hiding a node hides everything below it. The alternative — leaving orphaned
 * subtrees floating with no visible parent — would show reasoning whose
 * premise the user has explicitly asked not to see.
 */
export function computeVisible(
  session: Session,
  filters: Filters,
  childrenOf: Map<string, string[]>,
  playhead: number | null,
): Visibility {
  const upTo =
    playhead === null ? session.order.length : Math.max(1, Math.min(playhead, session.order.length))
  const inTime = new Set(session.order.slice(0, upTo))

  const visible = new Set<string>()
  const folded = new Map<string, number>()
  const root = session.order.find((id) => session.nodes[id]?.parentId === null)
  if (!root) return { visible, folded }

  const stack = [root]
  while (stack.length) {
    const id = stack.pop()!
    if (visible.has(id)) continue
    const n = session.nodes[id]
    if (!n) continue
    if (!inTime.has(id)) continue
    if (!passesFilters(n, filters)) continue

    visible.add(id)

    // The `continue` sits after the filter test above, so a folded branch the
    // user has filtered out stays filtered out rather than reappearing as a
    // stub.
    if (n.collapsed && n.parentId !== null) {
      let count = 0
      const st = [...(childrenOf.get(id) ?? [])]
      const seen = new Set<string>()
      while (st.length) {
        const k = st.pop()!
        if (seen.has(k)) continue
        seen.add(k)
        const m = session.nodes[k]
        if (!m || !inTime.has(k) || !passesFilters(m, filters)) continue
        count++
        if (m.collapsed) continue
        for (const g of childrenOf.get(k) ?? []) st.push(g)
      }
      folded.set(id, count)
      continue
    }

    for (const c of childrenOf.get(id) ?? []) stack.push(c)
  }

  return { visible, folded }
}

/** The subtree rooted at `id`, plus its ancestors — used by focus mode. */
export function focusSet(
  session: Session,
  childrenOf: Map<string, string[]>,
  id: string,
): Set<string> {
  const out = new Set<string>()
  const stack = [id]
  while (stack.length) {
    const cur = stack.pop()!
    if (out.has(cur)) continue
    out.add(cur)
    for (const c of childrenOf.get(cur) ?? []) stack.push(c)
  }
  let up = session.nodes[id]?.parentId ?? null
  const guard = new Set<string>()
  while (up && !guard.has(up)) {
    guard.add(up)
    out.add(up)
    up = session.nodes[up]?.parentId ?? null
  }
  return out
}
